import type { ResolvedSymmetry, SymmetryKind } from "@pdfking/filling"
import { FillFlip, GetBestFill, fill_len } from "@pdfking/filling"
import type { LayoutGenerator } from "@pdfking/filling-flex"
import { FillLayouts, FlipLayout, RotateLayout } from "@pdfking/filling-flex"
import type { Degree, Distance, Rectangle } from "@pdfking/math"
import { Floor, InfinityQ, Max, ToNumber, ToSpatial } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"

import type { DivisionId } from "#internal"
import type { ImpositionFill, ImpositionSchema, JobSchema } from "#internal"
import type { JobDestination } from "#internal"
import type { HookCtx, Hook } from "#internal"
import type { ImpositionLayout, ImpositionNodeData, ImpositionObject } from "#internal"
import type { PackedSheet, SheetInfo } from "#internal"
import type { Side } from "#internal"

import { CROSSMARK_LAYER_ID, CUT_LAYER_ID, HELP_LAYER_ID } from "../../../data/LayerList"
import { format_division_machine_size } from "../../../utils/FormatDivisionMachineSize"
import { DrawCutLines } from "../../common/grow/DrawCutLines"
import { AddDrawing } from "../../common/layout/AddDrawing"
import { DrawFillCrossmarks } from "../fill/DrawFillCrossmarks"
import { DrawFillHelp } from "../fill/DrawFillHelp"
import { BuildComponentGrow } from "./BuildComponentGrow"
import { remove_redundant_jobs } from "./PresetImpositionHelpers"
import { ResolveFillPlan } from "./ResolveFillPlan"

const LANDSCAPE_TURN = -90 as Degree

/** The layout under a path segment: what a page's identity is built from. */
const under = (page_prefix: number, layout: ImpositionLayout): ImpositionLayout => ({
  enum: "flex",
  page_prefix,
  layouts: [layout],
})

/** Stand a fill measured on the turned sheet back up on its machine size.
 *
 * Python passes `rotation=90` (concrete.py:62-65), which is clockwise on the
 * sheet because its y grows down. Here y grows up, so the same turn is the
 * negative one. */
const stand_on_machine = (
  object: ImpositionObject,
  machine_size: Rectangle<Distance>
): ImpositionObject =>
  object.size.width !== machine_size.width
    ? {
        ...object,
        layout: RotateLayout(object.layout, LANDSCAPE_TURN),
        size: { width: object.size.height, height: object.size.width },
      }
    : object

const apply = (
  processors: Hook[],
  ctx: HookCtx,
  layout: ImpositionLayout,
  select: (processor: Hook) => ((ctx: HookCtx, l: ImpositionLayout) => ImpositionLayout) | undefined
): ImpositionLayout =>
  processors.reduce((acc, processor) => {
    const hook = select(processor)
    return hook ? hook(ctx, acc) : acc
  }, layout)

/**
 * Procedural port of Python `_get_imposition_data` (concrete.py:148-413), one
 * sheet per (component × repetition × side).
 *
 * Every component is folded into its grow, the grow is filled onto each
 * printed job's machine size in turn — the answer of one fill the subject of
 * the next — the result stood up on its machine size, that laid out once per
 * repetition: the front as it is, the rear as the same layout turned over on
 * x, or one sheet when the fill already carries both sides.
 *
 * A page's identity is its path: component, repetition, subject, the page's
 * place in its grow, and its side. Every tile of one subject is the same grow
 * under the same prefix, so it is the same pages however many times it lands.
 */
export const BuildSheets = (
  input: ImpositionSchema,
  processors: Hook[],
  hook_ctx: HookCtx
): { sheets: PackedSheet[]; fills: ImpositionFill[] } => {
  const sheets: PackedSheet[] = []
  const fills: ImpositionFill[] = []
  // Each destination counts its own divisions.
  const division_counter = new Map<string, number>()
  // Every run of every component, in order: an offset plate's number.
  let repetition_counter = 0
  let component_counter = 0

  for (const component of input.component_array) {
    component_counter += 1
    const repetitions = component.repetitions || 1
    let subjects = component.subjects || 1
    const jobs = remove_redundant_jobs(component.job_array)
    // the copies each job's press sheet takes
    const job_fills = new Map<JobSchema, number>()
    if (jobs.length === 0) {
      fills.push({ fill_molt: 1, jobs: [] })
      continue
    }

    const print_front = jobs.some(j => Boolean(j.job_destination) && j.print_mode.is_front)
    const print_rear = jobs.some(j => Boolean(j.job_destination) && j.print_mode.is_rear)

    // Python: size = _get_size_for_component(...), and the inner-size hooks
    // wrap the grow before anything tiles it (concrete.py:245, 390).
    const grow = BuildComponentGrow(input, component, print_front, print_rear)
    let object: ImpositionObject = {
      ...grow,
      layout: apply(processors, hook_ctx, grow.layout, p => p.inner_size_processor),
    }

    let last_machine_size: Rectangle<Distance> | undefined
    let last_use_symmetry: SymmetryKind
    let last_division_id: DivisionId | undefined
    const accumulated_destinations: JobDestination[] = []
    const accumulated_divisions: DivisionId[] = []

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i]!
      const print_mode = job.print_mode
      const machine_size = job.machine_size
      const destination = job.job_destination
      if (!destination) continue
      const is_printed = print_mode.is_front || print_mode.is_rear
      let job_used_symmetry: ResolvedSymmetry = null

      const division: DivisionId = {
        counter: (division_counter.get(destination.id) ?? 0) + 1,
        label: component.label || "",
        machine_size: format_division_machine_size(machine_size),
        file_name: destination.file_name,
        used_symmetry: null,
        ...print_mode,
      }

      // Python: flip = cleaned_data[FLIP].pk == "on" (here the pk itself: on/off/same/rot)
      //         if i == 0: padding = component[PADDING] else: padding = EMPTY; flip = True
      // The machine size comes settled and oriented from `ValidateImpositionSizes`.
      const padding = i === 0 ? component.padding : ZERO_MARGINS
      const fill = GetBestFill(machine_size, object.size, {
        padding,
        security: job.margins,
        flip: i === 0 ? input.flip.id : FillFlip.FLIP_EFFICIENT,
        use_symmetry: print_mode.use_symmetry,
      })
      const plan = ResolveFillPlan(fill, job)
      job_used_symmetry = plan.used_symmetry
      division.used_symmetry = job_used_symmetry

      // Python: if subjects > m > 0: subjects = m
      const capacity = plan.capacity
      if (i === 0 && subjects > capacity && capacity > 0) subjects = capacity
      const count = i === 0 ? subjects : 1
      // Python `MultipleSubjectStrategy.create_job_provider`: the places are
      // shared out evenly, subject by subject, and what does not divide is
      // left out: the last places are the strip's end, so it only closes up
      // and adds no cut.
      const shared = Floor(capacity / count) * count
      const per_subject = shared / count
      job_fills.set(job, Max(per_subject, 1) * (plan.effective_print_mode.use_symmetry ? 2 : 1))
      const subject = object
      const generate: LayoutGenerator<ImpositionNodeData> = slot => {
        const place = ToNumber(slot)
        if (place > shared) return undefined
        return {
          ...subject,
          layout: under(Floor((place - 1) / per_subject), subject.layout),
        }
      }
      const result = FillLayouts(machine_size, object.size, {
        fill,
        generate,
        sides: "both",
        filling: component.filling.id,
        padding,
        security: job.margins,
        flip: i === 0 ? input.flip.id : FillFlip.FLIP_EFFICIENT,
        use_symmetry: print_mode.use_symmetry,
      })
      let layout: ImpositionLayout = {
        ...result.layout,
        data: {
          drawings: [],
          page: undefined,
          grow: undefined,
          fill: {
            job: { destination, division, print_mode },
            padding: component.padding,
            security: fill.security,
          },
        },
      }
      layout = AddDrawing(layout, CROSSMARK_LAYER_ID, DrawFillCrossmarks, true)
      layout = AddDrawing(layout, HELP_LAYER_ID, DrawFillHelp)
      // Python: strategy = machine_size_processor(strategy) (concrete.py:277),
      // on the first job's fill, before the sheet is stood up.
      if (i === 0) layout = apply(processors, hook_ctx, layout, p => p.machine_size_processor)
      object = { is_front: true, layout, size: result.size }
      last_machine_size = machine_size

      if (is_printed) {
        last_division_id = division
        division_counter.set(destination.id, division.counter)
        accumulated_destinations.push(destination)
        accumulated_divisions.push(division)
      }
      last_use_symmetry = print_mode.use_symmetry
    }

    // What the sheets are worked out of: a job dropped as a repeat of the one
    // before takes its sheets whole; the stock sheet takes the last press sheet,
    // one at least: no sheet is ever worked into none
    fills.push({
      fill_molt: InfinityQ(component.material.width)
        ? 1
        : Max(
            fill_len(GetBestFill(component.material, component.job_array.at(-1)!.machine_size)),
            1
          ),
      jobs: component.job_array.map(job => job_fills.get(job) ?? 1),
    })

    if (last_machine_size) object = stand_on_machine(object, last_machine_size)
    object = { ...object, layout: AddDrawing(object.layout, CUT_LAYER_ID, DrawCutLines, true) }

    // Python `_generate_pages_for_sheet_jobs`: one sheet when the fill turns
    // over on itself, else the front and then the rear turned over on x.
    const sides: Side[] = last_use_symmetry
      ? ["front"]
      : [...(print_front ? (["front"] as const) : []), ...(print_rear ? (["rear"] as const) : [])]

    for (let rep = 1; rep <= repetitions; rep++) {
      const front = under(component_counter, under(rep, object.layout))
      repetition_counter += 1
      for (const side of sides) {
        const info: SheetInfo = {
          slots: [],
          side,
          destinations: accumulated_destinations,
          destination_divisions: accumulated_divisions,
          division: last_division_id,
          position: ToSpatial(object.size),
          bleed: ZERO_MARGINS,
          repetition_number: repetition_counter,
        }
        sheets.push({
          layout: side === "front" ? front : FlipLayout(front, "x"),
          filling: component.filling.id,
          info,
        })
      }
    }
  }

  return { sheets, fills }
}
